import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import pLimit from "p-limit";

import { toast } from "components/ui/toast/Toast";
import { authClient, betterAuthErrorText } from "lib/auth/authClient";
import { reportToSentry, SKIP_SENTRY } from "lib/sentryErrors";

// better-auth returns this code when a pending invitation already exists for the email+org. We
// treat it as success: the invite is already out, so re-sending a partially-delivered batch is safe
// rather than a dead-end. (auth-service sets neither `resend` nor `cancelPendingInvitationsOnReInvite`,
// so a duplicate invite is a 400, never a second row.)
export const ALREADY_INVITED_CODE = "USER_IS_ALREADY_INVITED_TO_THIS_ORGANIZATION";

// better-auth's default rate limit (100 req / 10s per IP, shared across every auth-service route)
// is easy for a pasted-in list of emails to trip — there's no bulk invite endpoint, by design, to
// keep this surface unattractive for spamming. Cap concurrency instead of firing the whole batch at
// once, and let a handful of 429s recover with a short fixed backoff rather than failing outright.
// We don't have the actual `X-Retry-After` value at this layer (better-fetch's retry strategy only
// gets the attempt number, not the response), so the delay is a fixed guess, not computed from it.
const INVITE_CONCURRENCY = 10;
const INVITE_RETRY_OPTIONS = {
  type: "linear" as const,
  attempts: 3,
  delay: 2000,
  shouldRetry: (response: Response | null) => response?.status === 429,
};

const INVITE_PROGRESS_TOAST_ID = "sendBetterAuthInvites";

function invitesInFlightMessage(sent: number, total: number): string {
  return `Sending invite${total === 1 ? "" : "s"}… (${sent}/${total})`;
}

/**
 * Sends team invitations via the better-auth organization plugin. Each email becomes a pending
 * `invitation` row scoped to the inviter's active organization; the auth-service then emails the
 * invitee a link to the register flow. Accepting the invite (joining the company) happens when the
 * invitee signs up with that email — see CompanyResolverService in auth-service.
 *
 * We omit `organizationId`: better-auth falls back to the session's active organization, which the
 * auth-service sets for every session. Any member of it may invite someone as a member.
 *
 * Invites are sent independently, capped at `INVITE_CONCURRENCY` at a time via `p-limit` (rather
 * than all at once), so one failure doesn't hide the invites that did go out and a large pasted-in
 * list doesn't slam the rate limit. On partial failure we throw a summary the modal surfaces as a
 * toast; a fully successful batch resolves. A loading toast tracks how many have gone out so far.
 */
const SEND_INVITES_KEY = ["sendBetterAuthInvites"];

export function useSendBetterAuthInvites(
  options?: UseMutationOptions<void, unknown, string[], unknown>,
) {
  return useMutation({
    // Each failed invite is reported in mutationFn below; the summary it throws would duplicate them.
    meta: SKIP_SENTRY,
    mutationKey: SEND_INVITES_KEY,
    ...options,
    mutationFn: async (emails: string[]) => {
      const total = emails.length;
      let sent = 0;
      toast.loading(invitesInFlightMessage(sent, total), { id: INVITE_PROGRESS_TOAST_ID });

      const limit = pLimit(INVITE_CONCURRENCY);
      const results = await Promise.allSettled(
        emails.map((email) =>
          limit(async () => {
            const { error } = await authClient.organization
              .inviteMember({ email, role: "member" }, { retry: INVITE_RETRY_OPTIONS })
              .catch((thrown: unknown) => {
                // A network failure or abort throws rather than resolving with `error`.
                reportToSentry(thrown, SEND_INVITES_KEY);
                throw thrown;
              });
            sent += 1;
            toast.loading(invitesInFlightMessage(sent, total), { id: INVITE_PROGRESS_TOAST_ID });
            if (error && error.code !== ALREADY_INVITED_CODE) {
              if (!error.status || error.status >= 500) {
                reportToSentry(error, SEND_INVITES_KEY);
              }
              throw new Error(betterAuthErrorText(error, "Could not send invitation"));
            }
          }),
        ),
      );

      // One invite has one reason, so pass the server's own message on instead of a summary.
      const [only] = results;
      if (results.length === 1 && only.status === "rejected") {
        throw only.reason;
      }

      // allSettled preserves order, so results[i] maps back to emails[i].
      const failedEmails = emails.filter((_, i) => results[i].status === "rejected");
      if (failedEmails.length > 0) {
        throw new Error(
          failedEmails.length === emails.length
            ? "Could not send invitations"
            : `Sent ${emails.length - failedEmails.length} of ${emails.length} invitations. Couldn't invite ${failedEmails.join(", ")}`,
        );
      }
    },
    onSettled: (...args) => {
      toast.dismiss(INVITE_PROGRESS_TOAST_ID);
      return options?.onSettled?.(...args);
    },
  });
}
